/** @jsxImportSource @emotion/react */
import { Button, Icon, Link,ProgressCircle } from "@coreai/component-library";
import { css, useTheme } from "@emotion/react";
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "../../../../LanguageContext";
import { REACT_APP_NEW_THEME } from "services/config";
import axios from "axios";
import download from "../../../../assets/download.svg";

interface AnalyseHeaderProps {
  setNewAnalysis: (set: boolean) => void;
  showSeeAllLink: boolean;
  analysisData?: any;
  totalAnalyses: number;
}

export default function AnalysesHeader(props: Readonly<AnalyseHeaderProps>) {
  const theme = useTheme();
  const navigate = useNavigate();  
  const location = useLocation();
  const { t, language } = useLanguage();  
  const [searchParams] = useSearchParams();
  const [analysisId, setAnalysisId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean | false>(false);

  useEffect(() => {
    const id = searchParams.get("analysis");
    if (id && id != null) {
      setAnalysisId(id);
    }
  }, [searchParams]);

  const buttonStyle = css`
    width: 100%;
    font-size: 16px;
    background-color: transparent;
    color: ${theme.colors.primary};
    padding: 6px 24px;
    border: 1.2px solid ${theme.colors.primary};
    border-radius: 1.25rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s ease;
    min-width: 180px;

    :hover{
      color: ${theme.colors.primary};
      border: 1px solid ${theme.colors.primary};
    }

    @media (max-width: 768px) {
      font-size: 14px;
    }
  `;

  function slugify(text: string) {
    return text
      .toString()
      .toLowerCase()
      .replace(/\s+/g, '-')           // Replace spaces with -
      .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
      .replace(/\-\-+/g, '-')         // Replace multiple - with single -
      .replace(/^-+/, '')             // Trim - from start
      .replace(/-+$/, '');            // Trim - from end
  }
   const exportPDF2 = (async () => {
      setIsLoading(true); 
       const API_URL =  process.env.REACT_APP_API_BASE_URL || window.location.origin + "/api";
       let refData  = props.analysisData?.ruleset?.map((val: { stage: any; front_name: any }) => `${t(val.front_name)} ${(val.stage && !window.location.hostname.includes(REACT_APP_NEW_THEME)) ? `(${t(val.stage)})` : ""}`).join(', ');
       let frameworklink = "";
       let PDF_URL = `${API_URL}/analysis/downloadPdfAntigreen`;
        if(props.analysisData?.ruleset[0]?.category?.toLowerCase()!=='compliance' ){          
          PDF_URL = `${API_URL}/analysis/downloadPdfPositivere`;
        }

        const formattedDate = props.analysisData?.created_at ? new Date(props.analysisData?.created_at).toLocaleDateString(language, {
          weekday: "short",
          month: "short",
          day: "2-digit",
          year: "numeric",
        }).replace(/,/g, "") : '--';

        const id = searchParams.get("analysis");
        const languageCode =  props.analysisData?.locale_code;        
        let principle5_object = {}
        let principle9_object = {}
        let compliance_title = {}
        if(props.analysisData?.ruleset[0]?.worker_id?.toLowerCase()==='arpp-image-respect'){
          frameworklink = t("frameworks_arpp_image_respect_name_link", null, languageCode);
          principle5_object = {
          "frameworks_arpp_image_respect_title_principle_1": t("frameworks_arpp_image_respect_title_principle_1"),
          "frameworks_arpp_image_respect_title_principle_2": t("frameworks_arpp_image_respect_title_principle_2"),
          "frameworks_arpp_image_respect_title_principle_3": t("frameworks_arpp_image_respect_title_principle_3"),
          "frameworks_arpp_image_respect_title_principle_4": t("frameworks_arpp_image_respect_title_principle_4"),
          "frameworks_arpp_image_respect_title_principle_5": t("frameworks_arpp_image_respect_title_principle_5"),
          }          
        }

        if(props.analysisData?.ruleset[0]?.worker_id?.toLowerCase()==='arpp-greenwashing'){
          frameworklink = t("frameworks_arpp_greenwashing_name_link", null, languageCode);          
          principle9_object = {
          "frameworks_arpp_greenwashing_title_principle_1": t("frameworks_arpp_greenwashing_title_principle_1"),
          "frameworks_arpp_greenwashing_title_principle_2": t("frameworks_arpp_greenwashing_title_principle_2"),
          "frameworks_arpp_greenwashing_title_principle_3": t("frameworks_arpp_greenwashing_title_principle_3"),
          "frameworks_arpp_greenwashing_title_principle_4": t("frameworks_arpp_greenwashing_title_principle_4"),
          "frameworks_arpp_greenwashing_title_principle_5": t("frameworks_arpp_greenwashing_title_principle_5"),
          "frameworks_arpp_greenwashing_title_principle_6": t("frameworks_arpp_greenwashing_title_principle_6"),
          "frameworks_arpp_greenwashing_title_principle_7": t("frameworks_arpp_greenwashing_title_principle_7"),
          "frameworks_arpp_greenwashing_title_principle_8": t("frameworks_arpp_greenwashing_title_principle_8"),
          "frameworks_arpp_greenwashing_title_principle_9": t("frameworks_arpp_greenwashing_title_principle_9"),
          }          
        }
        if(props.analysisData?.ruleset[0]?.category?.toLowerCase()==='compliance') {
          compliance_title = {
            "compliance-1": t("compliance-1", null, languageCode),
            "compliance0": t("compliance0", null, languageCode),
            "compliance2": t("compliance2", null, languageCode),
            "compliance1": t("compliance1", null, languageCode),
          };
        }
        const response = await axios.post(`${PDF_URL}/${id}`, 
        {
          "projectLabel":t("analyse_result_card_title_project"),
          "advertiserLabel":t("analyse_result_card_title_advertiser"),
          "referenceframeLabel":t("analyse_result_card_title_referenceFrame"),
          "analysislanguageLabel":t("analyse_result_card_title_analysisLanguage"),
          "datecreatedLabel":t("analyse_result_card_title_dateCreated"),
          "inputLabel":t("analyse_result_card_title_input"),
          "noteLabel":t("analyse_result_card_title_note"),
          "formatedDate": formattedDate,
          "languageAnalyizedIn": props.analysisData?.locale_code === 'fr' ? t('label_french') : t('label_english'),
          "analyse_disclaimer_information": t("analyse_disclaimer_information", null, languageCode), 
          "campaign_analzyed_lable": t("campaign_analzyed_against_principles", null, languageCode),
          "campaignLabel":t("analyse_result_card_title_campaign", null, languageCode),
          "summaryCard":t("analyse_result_summaryCard_title", null, languageCode),
          "pdf_content_disclaimer":t("pdf_content_disclaimer", null, languageCode),
          "referenceframes": refData,
          "campaign_analysis_report": t("campaign_analysis_report", null, languageCode),
          "analysis_pdf_file": t("analysis_pdf_file", null, languageCode),
          "analysis_pdf_manual": t("analysis_pdf_manual", null, languageCode),
          "analysis_recall_link": t("analysis_recall_link", null, languageCode),
          "pdf_page_title": t("pdf_page_title", null, languageCode),
          "pdf_page_heading": t("pdf_page_heading", null, languageCode),
          "wrongs":t("analyse_result_detailedAnalysis_risk", null, languageCode),
          "corrects_if":t("analyse_result_detailedAnalysis_requesting", null, languageCode),
          "corrects":t("analyse_result_detailedAnalysis_compliant", null, languageCode),
          "not_applicable":t("analyse_result_detailedAnalysis_not_applicable", null, languageCode),
          "not_applicable_des":t("response_list_empty_not_applicable", null, languageCode),
          "response_list_empty_not_applicable":t("response_list_empty_not_applicable", null, languageCode),
          "frameworklink":frameworklink,
          "response_list_empty_corrects_if":t("response_list_empty_corrects_if", null, languageCode),
          "response_list_empty_wrongs":t("response_list_empty_wrongs", null, languageCode),
          "response_list_empty_corrects":t("response_list_empty_corrects", null, languageCode),
          "pdf_page_footer_ruleset":t("pdf_page_footer_ruleset", null, languageCode),
          "risk_flagging_words_header":t("risk_flagging_words_header", null, languageCode),
          "risk_flagging_words_high":t("risk_flagging_words_high", null, languageCode),
          "risk_flagging_words_medium":t("risk_flagging_words_medium", null, languageCode),
          "risk_flagging_words_low":t("risk_flagging_words_low", null, languageCode),
          "risk_flagging_words_unproductive":t("risk_flagging_words_unproductive", null, languageCode),
          "risk_flagging_words_awareness":t("risk_flagging_words_awareness", null, languageCode),
          "risk_flagging_words_title":t("risk_flagging_words_title", null, languageCode),
          "risk_flagging_words_number_of_matches":t("risk_flagging_words_number_of_matches", null, languageCode),
          "risk_flagging_words_source":t("risk_flagging_words_source", null, languageCode),
          "risk_flagging_words_risk_level":t("risk_flagging_words_risk_level", null, languageCode),
          "risk_flagging_words_term":t("risk_flagging_words_term", null, languageCode),
          "risk_flagging_rewrite_tip_title": t("risk_flagging_rewrite_tip_title", null, languageCode),
          ...principle9_object,
          ...principle5_object,   
          ...compliance_title,       
      }, {
        responseType: 'blob' // 👈 this tells Axios you're expecting a binary blob (PDF)
      });  
      
      setIsLoading(false);    
      const createdDate = new Date(props.analysisData.created_at).toISOString().slice(0, 10); // YYYY-MM-DD
      const file_name = slugify(`${createdDate}-${props.analysisData.client||''}-${props.analysisData.name}`);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${file_name}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);

   })

  const onClickNewAnalysis = () => {
    navigate(`/analyses/new`);
  };

  const onClickAnalysis = () => {
    navigate(`/analyses`);
  };
  const containerStyles = css`
    display: flex;
    flex-direction: column;
    margin-top: ${theme.spacing["5xl"]};
    // padding-top: ${theme.spacing["5xl"]};
  `;

  const mainContainerStyles = css`
    display: flex;
    align-items: center;
    justify-content: space-between;

    @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    }
  `;

  const backButtonContainerStyles = css`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: ${theme.spacing["lg"]};
    gap: 8px;
  `;

  const titleStyles = css`
    font-family: ${theme.typography.heading2_semiBold.fontFamily};
    font-size: ${theme.typography.heading2_semiBold.fontSize};
    font-weight: ${theme.typography.heading2_semiBold.fontWeight};
    line-height: ${theme.typography.heading2_semiBold.lineHeight};
    letter-spacing: ${theme.typography.heading2_semiBold.letterSpacing};
    color: ${theme.colors.textMedium};
  `;

  const linkStyles = css`
    font-family: ${theme.typography.body_semiBold.fontFamily};
    font-size: ${theme.typography.body_semiBold.fontSize};
    font-weight: ${theme.typography.body_semiBold.fontWeight};
    line-height: ${theme.typography.body_semiBold.lineHeight};
    text-decoration: none;
    color: ${theme.colors.primary};
    justify-content: center;
    align-items: flex-start;
    gap: ${theme.spacing.sm};
    cursor: pointer;
  `;

  const btnsStyle = css`
    display: flex;
    gap: 16px;

    @media (max-width: 768px) {
      gap: 8px;
      flex-direction: column;
      align-SELF: flex-end;
    }
  `;
  return (
    <div css={containerStyles}>
      {props.showSeeAllLink && (
        <div css={backButtonContainerStyles}>
          <Link css={linkStyles}  onClick={onClickAnalysis}>
            <Icon name="arrow-narrow-left" size="xs" />
            {t("analyse_see_all")}
          </Link>
        </div>
      )}

      <div css={mainContainerStyles}>
        <h2 css={titleStyles}>{props.showSeeAllLink? t("menu_analyses") : t("toolbar_analysis")}</h2>
        <div css={btnsStyle}>          
          {analysisId && (
            <Button
              variant={"secondary"}
              css={buttonStyle}
              icon={!isLoading ? <img src={download} alt="alert" /> : ''}
              color={theme.colors.primary}
              onClick={() => exportPDF2()}
              disabled={isLoading || props.analysisData === null}
            >
               {isLoading ? (
                <ProgressCircle size="xxs"/>
              ) : (
                t("response_buttonShare")
              )}
            </Button>
          )}
          {((location?.pathname ==='/analyses' || location?.pathname ==='/') || (location?.pathname.startsWith("/analyses/results"))) ? 
          <Button
            variant={"brand"}
            icon={"plus"}
            color={theme.colors.primary}
            onClick={onClickNewAnalysis }
            className="brandPrimaryBtn"
          >
            {t("navigation_newCampaign")}
          </Button> : null
        }
        </div>
      </div>
    </div>
  );
}
